import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Check, Scale } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ShoppingItem } from '../../types';
import { getUnitPriceCheck, hasUnitPriceAlert, formatUnitPrices, sizeFixUpdates } from '../../src/lib/basketItem';
import { useStore } from '../../src/lib/store';
import { unitPricingLawState } from '../../src/lib/pricingRights';

// Amber icon on an item whose shelf unit price doesn't match price ÷ size: in
// a basket line's action row (beside edit and delete) or beside a chat card's price.
export const UnitPriceIconButton: React.FC<{ item: ShoppingItem; isExpanded: boolean; onToggle: () => void }> = ({ item, isExpanded, onToggle }) => {
  const { t } = useTranslation();
  if (!hasUnitPriceAlert(item)) return null;
  const label = t('unit_price_check.badge', { defaultValue: "Unit price doesn't add up" });
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      aria-expanded={isExpanded}
      aria-label={label}
      title={label}
      className={`p-2 rounded-full transition-all shrink-0 text-amber-500 hover:text-amber-600 hover:bg-amber-50 ${isExpanded ? 'bg-amber-100' : ''}`}
    >
      <AlertTriangle size={14} />
    </button>
  );
};

/**
 * "Looks right": the tag is right, so the size was misread. Saves the size that
 * makes the tag add up (and the name and slug that carry it) through the
 * normal item update, so the basket, history and archive all get it; the check
 * then works out to a match. A new name gets a fresh embedding in the
 * background, as an edit does.
 */
export function applyLooksRight(item: ShoppingItem, onUpdate: (id: string, updates: Partial<ShoppingItem>) => void) {
  const fix = sizeFixUpdates(item);
  if (!fix) return;
  const renamed = !!fix.name && fix.name !== item.name;
  onUpdate(item.id, { ...fix, ...(renamed ? { embedding: null } : {}) });
  if (renamed) {
    import('../../services/geminiService')
      .then(({ generateEmbedding }) => generateEmbedding(fix.name!, undefined))
      .then((embedding) => { if (embedding?.length) onUpdate(item.id, { embedding }); })
      .catch((err) => console.warn('[UnitPriceCheck] embedding refresh failed (non-fatal):', err));
  }
}

// "Shelf says $0.31/oz · CartLens calculates $0.28/oz ($4.99 ÷ 18 oz)", with
// "Looks right", which sets the size the tag implies (the item's photo is
// already on its line). Only shown while the unit price doesn't add up.
// Rendered inside AnimatePresence.
export const UnitPriceCheckPanel: React.FC<{
  item: ShoppingItem;
  currencySymbol: string;
  /** Without it there's no "Looks right" button. */
  onLooksRight?: () => void;
  /** In a narrow chat card: tighter padding. */
  compact?: boolean;
}> = ({ item, currencySymbol, onLooksRight, compact }) => {
  const { t } = useTranslation();
  const storeAddress = useStore(state => state.currentStore?.address);
  const check = getUnitPriceCheck(item);
  if (!check || check.status !== 'mismatch') return null;
  const lawState = unitPricingLawState(storeAddress);
  const { shelf, calculated, formula } = formatUnitPrices(check, currencySymbol);
  const fix = onLooksRight ? sizeFixUpdates(item) : null;

  return (
    <div className={`bg-amber-50/70 rounded-[2rem] border border-amber-200/70 mt-2 ${compact ? 'p-3' : 'p-4'}`}>
      <div className="min-w-0">
        <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest">
          {t('unit_price_check.badge', { defaultValue: "Unit price doesn't add up" })}
        </p>
        <p className="text-sm font-bold text-[#1a2b3c] leading-snug mt-0.5">
          {check.usesItemPrice
            ? t('unit_price_check.shelf_says', { defaultValue: 'Shelf says {{shelf}}', shelf })
            : t('unit_price_check.shelf_says_regular', { defaultValue: 'Shelf says {{shelf}} for the regular price', shelf })}
          <span className="text-gray-300"> · </span>
          {t('unit_price_check.cartlens_calculates', { defaultValue: 'CartLens calculates {{calculated}}', calculated })}
          {' '}<span className="font-mono text-gray-500 whitespace-nowrap">({formula})</span>
        </p>
        <p className="text-[11px] text-gray-500 leading-snug mt-1">
          {t('unit_price_check.hint', { defaultValue: 'Worth a second look at the tag, or ask a staff member, before you pay.' })}
        </p>
        <p className="text-[11px] text-amber-800/80 leading-snug mt-2 flex items-start gap-1.5">
          <Scale size={12} className="shrink-0 mt-0.5" aria-hidden="true" />
          <span>
            {lawState
              ? t('unit_price_check.rights_state', { defaultValue: '{{state}} has a unit pricing law for many stores. Point the label out to the store, and check that the register charges the shelf price.', state: lawState })
              : t('unit_price_check.rights_general', { defaultValue: 'Point the label out to the store, and check that the register charges the shelf price. Keep the tag photo and your receipt in case you need them.' })}
          </span>
        </p>
        {fix && onLooksRight && (
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onLooksRight(); }}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-white border border-amber-200 text-amber-700 text-[10px] font-black uppercase tracking-widest hover:bg-amber-100 transition-colors"
            >
              <Check size={12} strokeWidth={3} />
              {t('unit_price_check.looks_right', { defaultValue: 'Looks right' })}
            </button>
            <span className="text-[11px] text-gray-500 leading-snug">
              {t('unit_price_check.looks_right_sets_size', { defaultValue: 'Sets the size to {{size}} to match the tag', size: `${fix.size} ${fix.unit}` })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// The panel, collapsible, for the CartAssistant's item cards (narrow: tighter padding).
export const UnitPriceCheckDetail: React.FC<{
  item: ShoppingItem;
  isExpanded: boolean;
  currencySymbol: string;
  onLooksRight?: () => void;
}> = ({ item, isExpanded, currencySymbol, onLooksRight }) => (
  <AnimatePresence>
    {isExpanded && hasUnitPriceAlert(item) && (
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        className="overflow-hidden"
      >
        <UnitPriceCheckPanel item={item} currencySymbol={currencySymbol} onLooksRight={onLooksRight} compact />
      </motion.div>
    )}
  </AnimatePresence>
);
